import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Default view implementation for related-tags feature (Bootstrap 5).
 *
 * Provides the presentation layer for the RelatedTagsRenderer component.
 * Exports React components for the tags container and toggle badge,
 * along with UI strings (messages) that can be overridden by projects.
 *
 * To customize the view for your project, create:
 *   projects/<projectId>/ui/hooks/relatedTagsView.tsx
 * and export a default function with the same signature.
 *
 * Use @core imports if you need to extend or wrap the core implementation:
 *   import coreGetRelatedTagsView from '@core/hooks/relatedTagsView';
 */
import React from 'react';
/**
 * Default messages for the related-tags UI.
 */
export const defaultRelatedTagsMessages = {
    getMoreLabel: (hiddenCount) => `+${hiddenCount} more`,
    lessLabel: 'Show less',
};
/**
 * Bootstrap 5 toggle badge component ("+N more" / "Show less").
 */
const RelatedTagsToggleBS5 = ({ hiddenCount, expanded, onToggle, messages, }) => {
    if (hiddenCount === 0 && !expanded)
        return null;
    const label = expanded ? messages.lessLabel : messages.getMoreLabel(hiddenCount);
    return (_jsx("a", { href: "#", className: "badge bg-secondary text-decoration-none tag-badge tag-badge-more", onClick: (e) => {
            e.preventDefault();
            onToggle();
        }, children: label }));
};
/**
 * Bootstrap 5 container component that renders the full related-tags block.
 *
 * Receives ToggleComponent via props (injected by the renderer) so that the
 * container never computes layout or duplicates the toggle rendering.
 */
const RelatedTagsContainerBS5 = ({ items, visibleCount, expanded, onToggle, messages, ToggleComponent, }) => {
    const hiddenCount = items.length - visibleCount;
    return (_jsxs("div", { className: "d-flex flex-wrap align-items-center gap-1", children: [items.map((item, index) => {
                const isVisible = expanded || index < visibleCount;
                return (_jsx("a", { href: item.href, className: `badge bg-secondary text-decoration-none tag-badge${isVisible ? ' d-inline-block' : ' d-none'}`, children: item.title }, item.href + item.title));
            }), _jsx(ToggleComponent, { hiddenCount: hiddenCount, expanded: expanded, onToggle: onToggle, messages: messages })] }));
};
/**
 * Default export: returns the view contract with BS5 components and default messages.
 */
export default function getRelatedTagsView() {
    const contract = {
        ContainerComponent: RelatedTagsContainerBS5,
        ToggleComponent: RelatedTagsToggleBS5,
        messages: defaultRelatedTagsMessages,
    };
    return contract;
}
